import { Fragment } from "react";
import type { Guidance, NodeTrace } from "../types";
import { GraphView } from "./GraphView";

interface Props {
  guidance: Guidance | null;
  activeNodes: NodeTrace[];
  thinking: boolean;
  error: string | null;
  onUseSuggestion: (text: string) => void;
}

const INTENT_LABELS: Record<string, string> = {
  identity_verification: "Identity check",
  knowledge_qa: "Cover question",
  claim_submission: "New claim",
  claim_status: "Claim status",
  escalation: "Complaint",
  smalltalk: "Conversation",
  unknown: "Not yet clear",
};

const SOURCE_LABELS: Record<string, string> = {
  classifier: "read locally, no model call",
  llm: "the model settled a close call",
  sticky: "carried over from the open workflow",
  rule: "fixed rule",
  none: "",
};

/** Which of the four verdict states the band should show, and what it says. */
function verdict(guidance: Guidance | null): { tone: string; ruling: string } {
  if (!guidance) {
    return { tone: "idle", ruling: "Nothing to act on yet." };
  }
  if (guidance.workflow === "locked") {
    return { tone: "halted", ruling: "Locked. Refer to the fraud team — do not retry in chat." };
  }
  if (guidance.requires_verification) {
    return {
      tone: "gated",
      ruling: "Identity not confirmed. Policy details cannot be discussed yet.",
    };
  }
  if (guidance.collected_information && Object.keys(guidance.collected_information).length > 0) {
    return { tone: "cleared", ruling: "Identity confirmed. Cleared to discuss the policy." };
  }
  return { tone: "cleared", ruling: "General enquiry. No identity check needed." };
}

/**
 * The Copilot: what the agent needs, ordered by how they work.
 *
 * The verdict band answers the only question that can get the agent in trouble - may I
 * discuss this customer's policy - before anything else on the surface. The drafted
 * reply is the single raised object, because it is the one thing here you act on.
 * Everything below is reference, separated by hairlines rather than boxed into cards
 * of equal weight, since they are not of equal weight.
 */
export function CopilotSidebar({
  guidance,
  activeNodes,
  thinking,
  error,
  onUseSuggestion,
}: Props) {
  const { tone, ruling } = verdict(guidance);
  const collected = Object.entries(guidance?.collected_information ?? {});
  const reading = guidance ? SOURCE_LABELS[guidance.intent_source] ?? "" : "";

  return (
    <aside className="sidebar">
      <header className="sidebar-header">
        <span>Copilot</span>
        <span>{thinking ? "reading…" : ""}</span>
      </header>

      <div className="sidebar-body">
        <div className={`verdict ${tone}`}>
          <div className="subject">
            {guidance ? (INTENT_LABELS[guidance.intent] ?? guidance.intent) : "Waiting"}
          </div>
          {guidance && (
            <div className="reading">
              <b>{(guidance.intent_confidence * 100).toFixed(0)}%</b> confident
              {reading ? `, ${reading}` : ""}
            </div>
          )}
          <div className="ruling">{ruling}</div>
        </div>

        {error && (
          <div className="notice bad">
            <b>Copilot could not finish that turn</b>
            {error}
          </div>
        )}

        {guidance?.degraded && (
          <div className="notice warn">
            <b>Part of this turn failed</b>
            The guidance below is usable but may be incomplete. Check it before sending.
          </div>
        )}

        {guidance?.warnings.map((warning) => (
          <div key={warning} className="notice warn">
            {warning}
          </div>
        ))}

        {!guidance && !thinking && (
          <p className="awaiting">
            Send a message as the customer and the Copilot will read it, work out what
            they need, and draft a reply for you to review.
          </p>
        )}

        {guidance?.suggested_response && (
          <div className="block reply">
            <h3>Reply to send</h3>
            <div className="draft">{guidance.suggested_response.text}</div>
            {guidance.suggested_response.rationale && (
              <p className="why">{guidance.suggested_response.rationale}</p>
            )}
            <div className="actions">
              <button
                className="insert"
                onClick={() => onUseSuggestion(guidance.suggested_response!.text)}
              >
                Insert into composer
              </button>
              {!guidance.suggested_response.model_authored && (
                <span className="provenance">Standard wording, not model-written</span>
              )}
            </div>
          </div>
        )}

        {guidance && guidance.missing_information.length > 0 && (
          <div className="block">
            <h3>Still to collect</h3>
            <ul className="needed">
              {guidance.missing_information.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {collected.length > 0 && (
          <div className="block">
            <h3>Confirmed so far</h3>
            <dl className="confirmed">
              {collected.map(([key, value]) => (
                <Fragment key={key}>
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </Fragment>
              ))}
            </dl>
          </div>
        )}

        {guidance && (
          <div className="block">
            <h3>Do next</h3>
            <p className="action">{guidance.next_action}</p>
          </div>
        )}

        {guidance && guidance.knowledge.length > 0 && (
          <div className="block">
            <h3>What this is based on</h3>
            {guidance.knowledge.map((citation) => (
              <div className="source" key={`${citation.source}-${citation.title}`}>
                <div className="title">{citation.title}</div>
                <div className="ref">{citation.source}</div>
                <p className="extract">{citation.snippet}</p>
              </div>
            ))}
          </div>
        )}

        {guidance?.summary && (
          <div className="block">
            <h3>Where the conversation has got to</h3>
            <p className="recap">{guidance.summary}</p>
          </div>
        )}

        {/* The diagram carries the workflow state, so there is no separate status card:
            the branch that lit up is which workflow ran. */}
        <div className="block">
          <h3>Workflow</h3>
          {guidance?.workflow_status && <p className="action">{guidance.workflow_status}</p>}
          <GraphView
            trace={thinking ? activeNodes : (guidance?.node_trace ?? [])}
            thinking={thinking}
          />
        </div>
      </div>
    </aside>
  );
}
